import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function About() {
  return (
    <div style={s.page}>
      <SEO
        title="About KnowFi — We Built This Because We Were Confused Too"
        description="KnowFi is a financial education platform. Not a bank. Not an advisor. Just plain-English tools and explanations for real people."
        path="/about"
      />
      <div style={s.glow} />

      <div style={s.inner}>

        {/* Hero */}
        <div style={s.hero}>
          <div style={s.heroIcon}>💡</div>
          <h1 style={s.heroTitle}>We built KnowFi because we were tired of being confused.</h1>
          <p style={s.heroSub}>
            Financial advice shouldn't require a finance degree. We built the tool we wish existed.
          </p>
        </div>

        {/* Problem Section */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>The problem we're solving</h2>
          <div style={s.sectionContent}>
            <p style={s.bodyText}>
              <strong style={{ color: '#f1f5f9' }}>47% of Americans can't pass a basic financial literacy test.</strong> That's not because people are bad at money — it's because the system has never really explained things clearly.
            </p>
            <div style={s.problemList}>
              <div style={s.problemItem}>
                <span style={s.problemIcon}>🏦</span>
                <div>
                  <div style={s.problemTitle}>Banks make money when you're confused</div>
                  <div style={s.problemDesc}>High-fee products, confusing terms, and fine print aren't accidents. Confusion is a feature, not a bug.</div>
                </div>
              </div>
              <div style={s.problemItem}>
                <span style={s.problemIcon}>📊</span>
                <div>
                  <div style={s.problemTitle}>Financial advisors charge 1–2% AUM and don't explain anything</div>
                  <div style={s.problemDesc}>You pay thousands per year, get a quarterly PDF, and still don't understand why they made each decision.</div>
                </div>
              </div>
              <div style={s.problemItem}>
                <span style={s.problemIcon}>🔍</span>
                <div>
                  <div style={s.problemTitle}>Google's first page for "how to invest" is mostly affiliate links</div>
                  <div style={s.problemDesc}>The sites ranking for financial terms often make money when you sign up — their incentives don't align with yours.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What KnowFi Is */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>What KnowFi actually is</h2>
          <div style={s.sectionContent}>
            <div style={s.identityCards}>
              <div style={s.identityCard}>
                <span style={s.identityCheck}>✅</span>
                <div>
                  <div style={s.identityTitle}>Not a bank. Not an advisor.</div>
                  <div style={s.identityDesc}>We're not trying to manage your money. We're trying to help you understand it.</div>
                </div>
              </div>
              <div style={s.identityCard}>
                <span style={s.identityCheck}>✅</span>
                <div>
                  <div style={s.identityTitle}>Not trying to sell you anything</div>
                  <div style={s.identityDesc}>Pro unlocks the full experience, but free gets you far. We're upfront about what's gated — no bait and switch.</div>
                </div>
              </div>
              <div style={s.identityCard}>
                <span style={s.identityCheck}>✅</span>
                <div>
                  <div style={s.identityTitle}>AI-powered plain-English explanations</div>
                  <div style={s.identityDesc}>Real financial knowledge, explained like a knowledgeable friend — not a textbook or a sales pitch.</div>
                </div>
              </div>
              <div style={s.identityCard}>
                <span style={s.identityCheck}>✅</span>
                <div>
                  <div style={s.identityTitle}>No commissions. No affiliate deals on the core product.</div>
                  <div style={s.identityDesc}>Our incentives are aligned with yours: we win when you understand your finances better.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Principles */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Our principles</h2>
          <div style={s.principleList}>
            {[
              { num: '01', title: 'Plain English always', desc: "If we can't explain it simply, we don't publish it. Jargon is a barrier, not a badge of honor." },
              { num: '02', title: 'No hidden agendas', desc: "We tell you what something is, how it works, and what to watch out for — not how to click our affiliate link." },
              { num: '03', title: 'Honest about what costs what', desc: "We're clear about what's free and what's Pro. No dark patterns, no hiding the price tag. You always know what you're getting." },
              { num: '04', title: 'Actual explanations, not just numbers', desc: "A calculator that gives you a number without context is useless. We explain what the number means and what to do about it." },
            ].map(p => (
              <div key={p.num} style={s.principle}>
                <div style={s.principleNum}>{p.num}</div>
                <div>
                  <div style={s.principleTitle}>{p.title}</div>
                  <div style={s.principleDesc}>{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div style={s.disclaimer}>
          <div style={s.disclaimerIcon}>⚠️</div>
          <div>
            <div style={s.disclaimerTitle}>Financial Disclaimer</div>
            <p style={s.disclaimerText}>
              KnowFi is for <strong>educational purposes only</strong>. Nothing here is financial advice. 
              All tools, calculators, articles, and AI-generated content are meant to help you learn and 
              think through your finances — not to replace professional guidance. Always consult a qualified 
              financial advisor before making investment, tax, or major financial decisions. Past performance 
              doesn't guarantee future results.
            </p>
          </div>
        </div>

        {/* Add to Home Screen */}
        <div style={s.pwaSection}>
          <div style={s.pwaHeader}>
            <span style={s.pwaIcon}>📱</span>
            <div>
              <div style={s.pwaTitle}>Use KnowFi like an app — no download required</div>
              <div style={s.pwaSub}>Add it to your home screen and it opens full-screen, just like a real app.</div>
            </div>
          </div>
          <div style={s.pwaSteps}>
            <div style={s.pwaStep}>
              <div style={s.pwaStepTitle}>On iPhone (Safari)</div>
              <div style={s.pwaStepDesc}>Tap the Share button (□↑) → scroll down → tap <strong style={{ color: '#e2e8f0' }}>"Add to Home Screen"</strong></div>
            </div>
            <div style={s.pwaStep}>
              <div style={s.pwaStepTitle}>On Android (Chrome)</div>
              <div style={s.pwaStepDesc}>Tap the 3-dot menu (⋮) → tap <strong style={{ color: '#e2e8f0' }}>"Add to Home Screen"</strong> or <strong style={{ color: '#e2e8f0' }}>"Install app"</strong></div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div style={s.cta}>
          <h2 style={s.ctaTitle}>Ready to actually understand your money?</h2>
          <p style={s.ctaSub}>Start with the KnowFi Brief — free market updates in plain English, every morning.</p>
          <Link to="/brief" style={s.ctaBtn}>Start with the KnowFi Brief →</Link>
        </div>

      </div>
    </div>
  );
}

const s = {
  page: {
    background: '#0f172a',
    color: '#f8fafc',
    minHeight: '100vh',
    padding: '60px 24px 80px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: 'inherit',
  },
  glow: {
    position: 'absolute',
    top: '-80px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '700px',
    height: '600px',
    background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.05) 0%, transparent 65%)',
    pointerEvents: 'none',
  },
  inner: {
    maxWidth: '760px',
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
  },

  // Hero
  hero: {
    textAlign: 'center',
    padding: '20px 0 48px',
    borderBottom: '1px solid #1e293b',
    marginBottom: '48px',
  },
  heroIcon: {
    fontSize: '52px',
    marginBottom: '20px',
    display: 'block',
  },
  heroTitle: {
    fontSize: 'clamp(26px, 4vw, 42px)',
    fontWeight: '900',
    margin: '0 0 16px',
    letterSpacing: '-0.5px',
    lineHeight: 1.2,
    color: '#f1f5f9',
  },
  heroSub: {
    color: '#64748b',
    fontSize: '18px',
    lineHeight: 1.6,
    margin: 0,
  },

  // Sections
  section: {
    marginBottom: '48px',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: '800',
    margin: '0 0 20px',
    color: '#f1f5f9',
    letterSpacing: '-0.3px',
  },
  sectionContent: {},
  bodyText: {
    color: '#94a3b8',
    fontSize: '16px',
    lineHeight: 1.7,
    margin: '0 0 24px',
  },

  // Problem items
  problemList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  problemItem: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    background: '#1e293b',
    borderRadius: '12px',
    padding: '18px 20px',
    border: '1px solid #334155',
  },
  problemIcon: {
    fontSize: '24px',
    flexShrink: 0,
    marginTop: '2px',
  },
  problemTitle: {
    color: '#e2e8f0',
    fontSize: '16px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  problemDesc: {
    color: '#64748b',
    fontSize: '14px',
    lineHeight: 1.55,
  },

  // Identity cards
  identityCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  identityCard: {
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start',
    padding: '16px 20px',
    background: 'rgba(56,189,248,0.04)',
    border: '1px solid rgba(56,189,248,0.1)',
    borderRadius: '12px',
  },
  identityCheck: {
    fontSize: '18px',
    flexShrink: 0,
    marginTop: '1px',
  },
  identityTitle: {
    color: '#e2e8f0',
    fontSize: '15px',
    fontWeight: '700',
    marginBottom: '3px',
  },
  identityDesc: {
    color: '#64748b',
    fontSize: '14px',
    lineHeight: 1.55,
  },

  // Principles
  principleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  principle: {
    display: 'flex',
    gap: '20px',
    alignItems: 'flex-start',
  },
  principleNum: {
    color: '#38bdf8',
    fontSize: '13px',
    fontWeight: '800',
    opacity: 0.6,
    minWidth: '24px',
    paddingTop: '3px',
    letterSpacing: '0.5px',
  },
  principleTitle: {
    color: '#f1f5f9',
    fontSize: '17px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  principleDesc: {
    color: '#64748b',
    fontSize: '14px',
    lineHeight: 1.6,
  },

  // Disclaimer
  disclaimer: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    background: 'rgba(251,191,36,0.06)',
    border: '1px solid rgba(251,191,36,0.2)',
    borderRadius: '14px',
    padding: '22px 24px',
    marginBottom: '48px',
  },
  disclaimerIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  disclaimerTitle: {
    color: '#fbbf24',
    fontSize: '14px',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  disclaimerText: {
    color: '#94a3b8',
    fontSize: '14px',
    lineHeight: 1.7,
    margin: 0,
  },

  // PWA / Add to Home Screen
  pwaSection: {
    background: 'rgba(99,102,241,0.06)',
    border: '1px solid rgba(99,102,241,0.15)',
    borderRadius: '14px',
    padding: '22px 24px',
    marginBottom: '32px',
  },
  pwaHeader: {
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  pwaIcon: {
    fontSize: '28px',
    flexShrink: 0,
  },
  pwaTitle: {
    color: '#e2e8f0',
    fontSize: '16px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  pwaSub: {
    color: '#64748b',
    fontSize: '14px',
    lineHeight: 1.5,
  },
  pwaSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingLeft: '42px',
  },
  pwaStep: {
    background: '#1e293b',
    borderRadius: '10px',
    padding: '12px 16px',
  },
  pwaStepTitle: {
    color: '#94a3b8',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  pwaStepDesc: {
    color: '#64748b',
    fontSize: '14px',
    lineHeight: 1.5,
  },

  // CTA
  cta: {
    textAlign: 'center',
    background: 'linear-gradient(135deg, rgba(56,189,248,0.08) 0%, rgba(99,102,241,0.06) 100%)',
    border: '1px solid rgba(56,189,248,0.15)',
    borderRadius: '20px',
    padding: '48px 32px',
    marginTop: '16px',
  },
  ctaTitle: {
    fontSize: 'clamp(20px, 3vw, 28px)',
    fontWeight: '900',
    margin: '0 0 12px',
    letterSpacing: '-0.3px',
  },
  ctaSub: {
    color: '#64748b',
    fontSize: '16px',
    margin: '0 0 24px',
    lineHeight: 1.6,
  },
  ctaBtn: {
    display: 'inline-block',
    background: '#38bdf8',
    color: '#0f172a',
    textDecoration: 'none',
    borderRadius: '12px',
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '800',
  },
};
